<template>
    <v-container>
        <v-row justify="center">
            <v-col md="12">
                <v-card>
                    <v-card-title class="d-block py-2 text-center font-weight-bold card bg--text" style="word-break: normal">
                        <v-icon color="bg">mdi-clock</v-icon> HISTORICO
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>
        <v-row justify="center">
            <v-col align-self="stretch" cols="12">
                <v-skeleton-loader
                    class="py-2 mx-auto" width="100%"
                    type="list-item-avatar"
                    v-if="loading === true"/>
                <v-alert v-else-if="country === 'VEN' && !prices.length" class="ma-0" dense outlined type="info">
                    No hay histórico BCV disponible en este momento.
                </v-alert>
                <v-card height="100%" v-else>
                    <v-card-text>
                        <h3 class="px-1 pb-2">
                            <v-icon>mdi-consolidate</v-icon> {{ dateLabel }}
                        </h3>
                        <v-menu
                            ref="menu"
                            v-model="menu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="date"
                                    name="date"
                                    prepend-inner-icon="mdi-calendar-cursor"
                                    hint="Ejemplo='año-mes-dia'"
                                    persistent-hint
                                    :error-messages="errors.date"
                                    :rules="[rules.req('date')]"
                                    :disabled="loading"
                                    @input="clearErrors('date')"
                                    readonly
                                    filled
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="date"
                                :active-picker.sync="activePicker"
                                color="primary"
                                :max="latestPrice ? latestPrice.date : undefined"
                                :min="minDate"
                                @change="save"
                            ></v-date-picker>
                        </v-menu>
                        <h3 class="px-1 pb-2">
                            <v-icon>mdi-compare-horizontal</v-icon> Comparar con
                        </h3>
                        <v-menu
                            ref="compareMenu"
                            v-model="compareMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="compareDate"
                                    name="compareDate"
                                    prepend-inner-icon="mdi-calendar-sync"
                                    hint="Fecha base de comparacion"
                                    persistent-hint
                                    :disabled="loading"
                                    readonly
                                    filled
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="compareDate"
                                :active-picker.sync="activeComparePicker"
                                color="primary"
                                :max="latestPrice ? latestPrice.date : undefined"
                                :min="minDate"
                                @change="saveCompare"
                            ></v-date-picker>
                        </v-menu>
                    </v-card-text>
                </v-card>
            </v-col>
            <template v-if="country === 'VEN'">
                <v-col align-self="stretch" cols="12" md="6">
                    <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line" v-if="loading === true"/>
                    <v-card height="100%" v-else>
                        <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                            <v-icon color="bg">mdi-bank</v-icon> Dolar Oficial BCV
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-alert v-if="!value" class="ma-4" dense outlined type="warning">
                            No hay una referencia BCV para la fecha seleccionada.
                        </v-alert>
                        <template v-else>
                            <v-card-text class="text-center text-h4 font-weight-bold pb-1 bg--text">
                                {{ formatVes(value) }} VES
                            </v-card-text>
                            <v-card-text class="text-center pb-1">
                                Referencia oficial del {{ date | moment("dddd LL") }}
                            </v-card-text>
                            <v-card-text :class="'text-center font-weight-bold py-1 ' + trendInfo.color + '--text'">
                                {{ trendInfo.text }}
                                <v-icon :color="trendInfo.color">mdi-menu-{{ trendInfo.icon }}</v-icon>
                            </v-card-text>
                        </template>
                    </v-card>
                </v-col>
                <v-col align-self="stretch" cols="12" md="6">
                    <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line" v-if="loading === true"/>
                    <v-card height="100%" v-else>
                        <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                            <v-icon color="bg">mdi-chart-line</v-icon> RESUMEN
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="text-center pb-1">
                            Esta es la referencia oficial BCV para la fecha seleccionada.
                        </v-card-text>
                        <v-card-text class="text-center pt-1" v-if="value">
                            $1.00 USD equivale a {{ formatVes(value) }} VES.
                        </v-card-text>
                        <v-card-text :class="'text-center font-weight-bold pt-1 ' + compareInfo.color + '--text'" v-if="comparePrice">
                            {{ compareInfo.text }}
                        </v-card-text>
                        <v-sheet v-if="chartValues.length > 1" color="transparent" class="px-4 pb-4">
                            <v-sparkline
                                :value="chartValues"
                                :gradient="chartGradient"
                                :smooth="8"
                                :padding="8"
                                :line-width="2"
                                auto-draw
                                height="72"
                            ></v-sparkline>
                        </v-sheet>
                    </v-card>
                </v-col>
                <v-col v-for="i in venezuelaTabs" :key="i.name" cols="12" md="6">
                    <v-card>
                        <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text">
                            Tabla de {{ i.from }} a {{ i.to }}
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-list class="px-2" dense>
                            <template v-for="v in i.values">
                                <v-list-item :key="'VEN' + i.name + v" class="trunck-text" style="height: 30px" dense>
                                    <v-list-item-content class="d-block text-right">
                                        {{ i.fromSymbol + Number(v).toLocaleString("en-US") + ' ' + i.from }}
                                    </v-list-item-content>
                                    <v-list-item-icon class="d-block text-center px-2">
                                        <v-icon>mdi-arrow-right-circle</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content class="d-block">
                                        {{ formatVenConversion(i, v) }}
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider v-if="v !== i.values[i.values.length-1]"></v-divider>
                            </template>
                        </v-list>
                    </v-card>
                </v-col>
            </template>
            <template v-if="country !== 'VEN'">
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line" v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                        <v-icon color="bg">mdi-calendar</v-icon> Dólar (TRM)
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="text-center text-h4 font-weight-bold pb-1 bg--text">
                        {{ currency.symbol + Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency.code }}
                    </v-card-text>
                    <v-card-text class="text-center pb-1">
                        Esta es la TRM vigente del {{ date | moment("dddd LL") }}
                    </v-card-text>
                    <v-card-text :class="'text-center font-weight-bold py-1 ' + trendInfo.color + '--text'">
                        {{ trendInfo.text }}
                        <v-icon :color="trendInfo.color">mdi-menu-{{ trendInfo.icon }}</v-icon>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line" v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                        <v-icon color="bg">mdi-text</v-icon> RESUMEN
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="text-center pb-1">
                        Esta es la TRM vigente del {{ date | moment("dddd LL") }}
                    </v-card-text>
                    <v-card-text class="text-center pt-1">
                        {{ currency.symbol }}1.00 {{ currency.code }} (un dólar estadounidense) equivale a {{ currencyResult.symbol + Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currencyResult.code }} ({{ toWords(value) }} pesos colombianos)
                    </v-card-text>
                    <v-card-text :class="'text-center font-weight-bold pt-1 ' + compareInfo.color + '--text'" v-if="comparePrice">
                        {{ compareInfo.text }}
                    </v-card-text>
                    <v-sheet v-if="chartValues.length > 1" color="transparent" class="px-4 pb-4">
                        <v-sparkline
                            :value="chartValues"
                            :gradient="chartGradient"
                            :smooth="8"
                            :padding="8"
                            :line-width="2"
                            auto-draw
                            height="72"
                        ></v-sparkline>
                    </v-sheet>
                </v-card>
            </v-col>
            <v-col cols="12" v-if="loading === true">
                <v-skeleton-loader class="py-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line"/>
            </v-col>
            <v-col v-for="i in tabs" :key="i.name" cols="12" md="6" v-else>
                <v-card>
                    <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text">
                        Tabla de {{ i.from }} a {{ i.to }}
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-list class="px-2" dense>
                        <template v-for="v in i.values">
                            <v-list-item :key="'V' + v" class="trunck-text" style="height: 30px" dense>
                                <v-list-item-content class="d-block text-right">
                                    {{ i.fromSymbol + Number(v).toLocaleString("en-US") + ' ' + i.from }}
                                </v-list-item-content>
                                <v-list-item-icon class="d-block text-center px-2">
                                    <v-icon>mdi-arrow-right-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="d-block">
                                    {{ i.toSymbol + Number(i.to === 'COP' ? v*value : v/value).toLocaleString("en-US", {minimumFractionDigits: (i.to === 'COP' && (v*value) > 999999 ? 0 : 2), maximumFractionDigits: (i.to === 'COP' && (v*value) > 999999 ? 0 : 2)}) + ' ' + i.to }}
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider v-if="v !== i.values[i.values.length-1]"></v-divider>
                        </template>
                    </v-list>
                </v-card>
            </v-col>
            </template>
        </v-row>
    </v-container>
</template>

<script>
import Form from "../helpers/form";
import { toWords } from "n2words/es";

    export default {
        name: "History",
        mixins: [ Form ],
        props: [ 'country', 'items', 'load' ],
        data: () => ({
            labels: {
                date: 'Fecha TRM'
            },
            loading: true,
            prices: [],
            activePicker: null,
            activeComparePicker: null,
            date: null,
            compareDate: null,
            menu: false,
            compareMenu: false,
            value: 0,
            chartGradient: ['#0D93A5', '#DFF1F7'],
            currency: {
                symbol: '$',
                code: 'USD'
            },
            currencyResult: {
                symbol: '$',
                code: 'COP'
            },
            tabs: [
                {
                    from: 'USD',
                    fromSymbol: '$',
                    to: 'COP',
                    toSymbol: '$',
                    values: [ 1, 2, 5, 10, 15, 20, 25, 50, 100, 200, 500, 1000, 2000, 5000, 10000 ]
                }, {
                    from: 'COP',
                    fromSymbol: '$',
                    to: 'USD',
                    toSymbol: '$',
                    values: [ 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000, 2000000, 5000000, 10000000, 20000000, 50000000 ]
                },
            ],
            venezuelaTabs: [
                {
                    name: 'usd-ves',
                    from: 'USD',
                    fromSymbol: '$',
                    to: 'VES',
                    toSymbol: 'Bs. ',
                    values: [ 1, 5, 10, 25, 50, 100, 200, 500, 1000, 5000 ]
                }, {
                    name: 'ves-usd',
                    from: 'VES',
                    fromSymbol: 'Bs. ',
                    to: 'USD',
                    toSymbol: '$',
                    values: [ 100, 200, 500, 1000, 5000, 10000, 20000, 50000, 100000, 500000 ]
                },
            ]
        }),
        mounted() {
            this.getData();
        },
        watch: {
            load() {
                this.loading = this.load;
            },
	            items() {
	                const items = Array.isArray(this.items) ? this.items : [];
	                this.prices = items;
	                this.date = items.length ? items[0].date : null;
	                this.compareDate = items.length > 1 ? items[1].date : this.date;
            },
            country() {
                this.getData();
            },
            menu(val) {
                val && setTimeout(() => (this.activePicker = 'YEAR'))
            },
            compareMenu(val) {
                val && setTimeout(() => (this.activeComparePicker = 'YEAR'))
            },
	            date() {
	                this.getResult();
	            }
	        },
	        computed: {
                dateLabel() {
                    return this.country === 'VEN' ? 'Fecha BCV' : this.labels.date;
                },
	            latestPrice() {
	                return this.prices.length ? this.prices[0] : null;
	            },
                selectedPrice() {
                    return this.prices.find(({ date }) => date === this.date) || null;
                },
                comparePrice() {
                    return this.prices.find(({ date }) => date === this.compareDate) || null;
                },
                chartValues() {
                    return this.prices
                        .slice(0, 14)
                        .reverse()
                        .map(item => Number(item.value || 0));
                },
                previousPrice() {
                    if (!this.selectedPrice) {
                        return null;
                    }

                    const index = this.prices.findIndex(({ date }) => date === this.date);

                    return index >= 0 ? this.prices[index + 1] || null : null;
                },
                trendInfo() {
                    if (!this.selectedPrice || !this.previousPrice) {
                        return {
                            color: 'primary',
                            icon: 'right',
                            text: 'No hay una fecha anterior disponible para comparar.'
                        };
                    }

                    const difference = Number(this.selectedPrice.value) - Number(this.previousPrice.value);
                    const color = difference >= 0 ? 'success' : 'error';
                    const icon = difference >= 0 ? 'up' : 'down';
                    const verb = difference >= 0 ? 'subio' : 'bajo';

                    return {
                        color,
                        icon,
                        text: 'Frente al registro anterior ' + verb + ' ' + this.formatRateValue(Math.abs(difference)) + '.'
                    };
                },
                compareInfo() {
                    if (!this.selectedPrice || !this.comparePrice) {
                        return {
                            color: 'primary',
                            text: ''
                        };
                    }

                    const difference = Number(this.selectedPrice.value) - Number(this.comparePrice.value);
                    const percent = Number(this.comparePrice.value) ? (difference * 100) / Number(this.comparePrice.value) : 0;
                    const color = difference >= 0 ? 'success' : 'error';
                    const sign = difference >= 0 ? '+' : '-';

                    return {
                        color,
                        text: 'Comparado con ' + this.compareDate + ': ' + sign + this.formatRateValue(Math.abs(difference)) + ' (' + sign + Number(Math.abs(percent)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%).'
                    };
                },
                minDate() {
                    if (this.country === 'VEN' && this.prices.length) {
                        return this.prices[this.prices.length - 1].date;
                    }

                    return '1991-12-02';
                }
	        },
	        methods: {
            toWords,
	            getData() {
	                if (this.load === false) {
	                    const items = Array.isArray(this.items) ? this.items : [];
	                    this.prices = items;
	                    this.date = items.length ? items[0].date : null;
	                    this.compareDate = items.length > 1 ? items[1].date : this.date;
	                    this.loading = this.load;
	                }
            },
            save (date) {
                this.$refs.menu.save(date)
            },
            saveCompare (date) {
                this.$refs.compareMenu.save(date)
            },
	            getResult() {
	                if (!this.date || !this.prices.length) {
	                    this.value = 0;
	                    return;
	                }

	                const data = this.prices.find( ({ date }) => date === this.date);
	                this.value = data ? data.value : 0;
	            },
                formatRateValue(value) {
                    if (this.country === 'VEN') {
                        return this.formatVes(value) + ' VES';
                    }

                    return '$' + Number(value || 0).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }) + ' COP';
                },
                formatVes(value) {
                    return 'Bs. ' + Number(value || 0).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });
                },
                formatUsd(value) {
                    return '$' + Number(value || 0).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });
                },
                formatVenConversion(item, value) {
                    if (!this.value) {
                        return item.toSymbol + '0.00 ' + item.to;
                    }

                    if (item.to === 'VES') {
                        return this.formatVes(value * this.value) + ' VES';
                    }

                    return this.formatUsd(value / this.value) + ' USD';
                }
        }
    }
</script>

<style scoped>

</style>
